import type React from "react";
import { lazy, memo, Suspense, useEffect, useMemo } from "react";
import { useSession } from "../../auth/SessionProvider";
import {
  LAYOUT_TYPE_BLANK,
  LAYOUT_TYPE_CLASSIC,
  LAYOUT_TYPE_DECKED,
  LAYOUT_TYPE_LOADING,
  LAYOUT_TYPE_MODERN,
  LAYOUT_TYPE_SIMPLE,
  LAYOUT_TYPE_STACKED_SIDE,
} from "../../constants/theme.constant";
import { isUnassignedCustomer } from "../../domain/authenticated-user/access";
import store from "../../store";
import { clearCompanyScopedData } from "../../store/rootReducer";
import { useTheme } from "../../theme/ThemeProvider";
import useAuth from "../../utils/hooks/useAuth";
import useDirection from "../../utils/hooks/useDirection";
import useLocale from "../../utils/hooks/useLocale";
import NoCompanyAccess from "../../views/no-company-access";
import Loading from "../shared/Loading";

type LayoutType = keyof typeof layouts;

const layouts = {
  [LAYOUT_TYPE_CLASSIC]: lazy(() => import("./ClassicLayout")),
  [LAYOUT_TYPE_MODERN]: lazy(() => import("./ModernLayout")),
  [LAYOUT_TYPE_STACKED_SIDE]: lazy(() => import("./StackedSideLayout")),
  [LAYOUT_TYPE_SIMPLE]: lazy(() => import("./SimpleLayout")),
  [LAYOUT_TYPE_DECKED]: lazy(() => import("./DeckedLayout")),
  [LAYOUT_TYPE_BLANK]: lazy(() => import("./BlankLayout")),
  [LAYOUT_TYPE_LOADING]: lazy(() => import("./LoadingLayout")),
};

const Layout = ({ content }: { content?: React.ReactNode }) => {
  const { layout } = useTheme();
  const layoutType = layout.type as LayoutType | "loading";
  const { previousType } = layout;
  const { authenticated } = useAuth();
  const { user } = useSession();
  const pathname = window.location.pathname;
  const previewAccessFlow =
    pathname === "/preview-access-denied" ||
    /^\/answer\/[^/]+\/preview\/?$/.test(pathname);
  const unassignedCustomer = isUnassignedCustomer(user);

  useDirection();
  useLocale();

  useEffect(() => {
    if (unassignedCustomer) store.dispatch(clearCompanyScopedData());
  }, [unassignedCustomer]);

  const AppLayout = useMemo(() => {
    if (layoutType === "loading" || previousType === undefined) {
      // this is the firts page load
      return layouts[LAYOUT_TYPE_LOADING];
    } else if (previousType !== layoutType) {
      return layouts[LAYOUT_TYPE_LOADING];
    } else {
      if (authenticated) {
        return (
          layouts[layoutType as LayoutType] ?? layouts[LAYOUT_TYPE_LOADING]
        );
      }
      return lazy(() => import("./AuthLayout"));
    }
  }, [layoutType, previousType, authenticated]);

  if (
    unassignedCustomer &&
    pathname !== "/company-invitation" &&
    !previewAccessFlow
  ) {
    return <NoCompanyAccess />;
  }

  return (
    <Suspense
      fallback={
        <div className="flex flex-auto flex-col h-[100vh]">
          <Loading loading={true} />
        </div>
      }
    >
      <AppLayout content={content} />
    </Suspense>
  );
};

export default memo(Layout);
